import React, { useState, useEffect } from 'react';

const CLOUDINARY_UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'vastra';
const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'dgfwqjq8p';

const ImageUpload = ({ 
  onImagesChange, 
  multiple = false, 
  accept = 'image/*,video/*',
  maxFiles = 5,
  className = '',
  returnMeta = false,
  initialImages = []
}) => {
  const [previewUrls, setPreviewUrls] = useState(initialImages);
  const [uploading, setUploading] = useState(false);

  // Update internal state when initialImages prop changes (e.g., when editing a different banner)
  useEffect(() => {
    setPreviewUrls(initialImages);
  }, [initialImages]);

  const uploadToCloudinary = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);

    try {
    const res = await fetch(
      `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/auto/upload`,
      { method: 'POST', body: formData }
    );
    const data = await res.json();
      if (returnMeta) {
        return {
          url: data.secure_url,
          type: data.resource_type === 'video' ? 'video' : 'image',
          publicId: data.public_id
        };
      }
    return data.secure_url;
    } catch (error) {
      console.error('Error uploading to Cloudinary:', error);
      throw error;
    }
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    
    // Check if total files exceed maxFiles BEFORE starting upload
    if (files.length + previewUrls.length > maxFiles) {
      alert(`You can only upload up to ${maxFiles} files in total.`);
      return;
    }

    setUploading(true);
    try {
      const uploadPromises = files.map(uploadToCloudinary);
      const newUploadedMeta = await Promise.all(uploadPromises); // These are the new Cloudinary meta objects

      // Combine existing images/videos with newly uploaded ones
      const updatedImages = [...previewUrls, ...newUploadedMeta].slice(0, maxFiles);
      setPreviewUrls(updatedImages);
      onImagesChange(updatedImages);
    } catch (err) {
      console.error('Upload failed:', err);
      alert('Upload failed. Please try again.');
    }
    setUploading(false);
  };

  const removeImage = (index) => {
    const newUrls = previewUrls.filter((_, i) => i !== index);
    setPreviewUrls(newUrls);
    onImagesChange(newUrls);
  };

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="flex items-center justify-center w-full">
        <label className="flex flex-col items-center justify-center w-full h-64 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
          <div className="flex flex-col items-center justify-center pt-5 pb-6">
            <svg className="w-8 h-8 mb-4 text-gray-500" aria-hidden="true" fill="none" viewBox="0 0 20 16">
              <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"/>
            </svg>
            <p className="mb-2 text-sm text-gray-500">
              <span className="font-semibold">Click to upload</span> or drag and drop
            </p>
            <p className="text-xs text-gray-500">
              {multiple ? `PNG, JPG, GIF, MP4, WEBM (MAX. ${maxFiles} files)` : 'PNG, JPG, GIF, MP4, WEBM'}
            </p>
            {uploading && <span className="text-blue-500 mt-2">Uploading...</span>}
          </div>
          <input 
            type="file" 
            className="hidden" 
            multiple={multiple}
            accept={accept}
            onChange={handleFileChange}
            disabled={uploading || previewUrls.length >= maxFiles}
          />
        </label>
      </div>
      {previewUrls.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {previewUrls.map((item, index) => {
            if (!item) return null;
            
            // Handle both string URLs and object URLs
            let url, type;
            if (typeof item === 'string') {
              url = item;
              type = 'image'; // Default to image for string URLs
            } else if (item && typeof item === 'object') {
              url = item.url;
              type = item.type || 'image';
            } else {
              return null;
            }
            
            if (!url) return null;
            return (
              <div key={index} className="relative aspect-square group">
                {type === 'video' || (typeof url === 'string' && url.match(/\.(mp4|webm|ogg)$/i)) ? (
                <video src={url} controls className="w-full h-full object-cover rounded-lg" />
              ) : (
                <img src={url} alt={`Preview ${index + 1}`} className="w-full h-full object-cover rounded-lg" />
              )}
                <button
                  type="button"
                  onClick={() => removeImage(index)}
                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                  aria-label="Remove image"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ImageUpload; 